import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { ComputerScreen } from './ComputerScreen';
import { Workstation, InteractiveTarget } from './Workstation';
import { Room } from './Room';
import { Hologram } from './Hologram';
import { EXPERIENCES, Experience } from '../data/experiences';
import { sound } from '../audio/SoundEngine';

export type CameraPreset = 'desk' | 'panoramic' | 'window' | 'autotour';

export class World {
  public container: HTMLElement;
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public renderer: THREE.WebGLRenderer;
  public controls: OrbitControls;

  // Scene entities
  public computerScreen: ComputerScreen;
  public workstation: Workstation;
  public room: Room;
  public hologram: Hologram;

  // Camera preset positions and targets
  private activePreset: CameraPreset = 'desk';
  private targetCameraPos: THREE.Vector3 = new THREE.Vector3(0, 1.05, 1.45);
  private targetLookAt: THREE.Vector3 = new THREE.Vector3(0, 1.05, -0.3);
  private currentLookAt: THREE.Vector3 = new THREE.Vector3(0, 1.05, -0.3);
  private isAutoTouring: boolean = false;
  private autoTourTime: number = 0;

  // Interaction & Raycasting
  private raycaster: THREE.Raycaster = new THREE.Raycaster();
  private mouse: THREE.Vector2 = new THREE.Vector2(-999, -999);
  private hoveredTarget: InteractiveTarget | null = null;

  // Selection state
  public currentExperience: Experience = EXPERIENCES.racer;
  public onExperienceChange?: (exp: Experience) => void;
  public onHoverChange?: (target: InteractiveTarget | null) => void;
  public onScreenClick?: () => void;

  private clock: THREE.Clock = new THREE.Clock();

  constructor(container: HTMLElement) {
    this.container = container;

    // 1. Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x060810);
    this.scene.fog = new THREE.FogExp2(0x060810, 0.04);

    // 2. Camera
    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(52, aspect, 0.1, 100);
    this.camera.position.set(0, 1.05, 1.45);

    // 3. Renderer
    this.renderer = new THREE.WebGLRenderer({
      powerPreference: 'high-performance',
      antialias: true,
      stencil: false,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.container.appendChild(this.renderer.domElement);

    // 4. Orbit Controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2 + 0.05;
    this.controls.minDistance = 0.8;
    this.controls.maxDistance = 7.5;
    this.controls.target.copy(this.currentLookAt);

    // 5. Instantiate Subsystems
    this.computerScreen = new ComputerScreen();
    this.workstation = new Workstation(this.computerScreen);
    this.room = new Room();
    this.hologram = new Hologram();

    this.scene.add(this.room.group);
    this.scene.add(this.workstation.group);
    this.scene.add(this.hologram.group);

    // 6. Event Listeners
    this.setupEvents();

    // Default to DESK focus preset for immersive computer experience
    this.setCameraPreset('desk');
  }

  public setCameraPreset(preset: CameraPreset) {
    this.activePreset = preset;
    this.isAutoTouring = preset === 'autotour';

    if (preset === 'desk') {
      this.targetCameraPos.set(0, 1.05, 1.45);
      this.targetLookAt.set(0, 1.05, -0.3);
    } else if (preset === 'panoramic') {
      this.targetCameraPos.set(0, 1.6, 3.2);
      this.targetLookAt.set(0, 0.8, -0.2);
    } else if (preset === 'window') {
      this.targetCameraPos.set(-2.0, 1.5, 1.8);
      this.targetLookAt.set(0.5, 2.2, -8.0);
    }

    sound.playClick();
  }

  public selectExperience(id: 'racer' | 'ide' | 'fps') {
    const exp = EXPERIENCES[id];
    if (!exp) return;
    this.currentExperience = exp;

    this.computerScreen.setExperience(exp);
    this.hologram.setMode(id);
    this.workstation.animateCartridgeInsert(id);

    if (this.onExperienceChange) {
      this.onExperienceChange(exp);
    }
  }

  private setupEvents() {
    window.addEventListener('resize', this.onResize);

    const dom = this.renderer.domElement;
    dom.addEventListener('pointermove', this.onPointerMove);
    dom.addEventListener('click', this.onClick);

    // Disable auto-tour on manual drag
    this.controls.addEventListener('start', () => {
      if (this.isAutoTouring) {
        this.isAutoTouring = false;
        this.activePreset = 'panoramic';
      }
    });
  }

  private onResize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  };

  private onPointerMove = (e: MouseEvent) => {
    this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;

    // Raycast check
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.workstation.interactiveObjects, true);

    if (intersects.length > 0) {
      let hitObj: THREE.Object3D | null = intersects[0].object;
      while (hitObj && !this.workstation.targetMap.has(hitObj)) {
        hitObj = hitObj.parent;
      }

      if (hitObj && this.workstation.targetMap.has(hitObj)) {
        const target = this.workstation.targetMap.get(hitObj)!;
        if (this.hoveredTarget !== target) {
          this.hoveredTarget = target;
          this.renderer.domElement.style.cursor = 'pointer';
          if (this.onHoverChange) this.onHoverChange(target);
          sound.playKeyType();
        }
        return;
      }
    }

    if (this.hoveredTarget !== null) {
      this.hoveredTarget = null;
      this.renderer.domElement.style.cursor = 'default';
      if (this.onHoverChange) this.onHoverChange(null);
    }
  };

  private onClick = () => {
    // Resume audio on first interaction
    sound.startAtmosphere();

    if (!this.hoveredTarget) return;

    const { type } = this.hoveredTarget;
    if (type === 'screen') {
      if (this.activePreset !== 'desk') {
        this.setCameraPreset('desk');
      } else {
        if (this.onScreenClick) this.onScreenClick();
      }
    } else if (type === 'cartridge_racer') {
      this.selectExperience('racer');
    } else if (type === 'cartridge_ide') {
      this.selectExperience('ide');
    } else if (type === 'cartridge_fps') {
      this.selectExperience('fps');
    } else if (type === 'pc_tower') {
      sound.playLaunchWarp();
      this.computerScreen.addLog('TURBO RIG OVERCLOCK ACTIVATED [MAX CLOCKS]');
    } else if (type === 'keyboard') {
      sound.playKeyType();
      this.computerScreen.addLog('TERMINAL KEYSTROKE CAPTURED');
    }
  };

  public update() {
    const deltaTime = this.clock.getDelta();

    // 1. Update Camera towards presets
    if (this.isAutoTouring) {
      this.autoTourTime += deltaTime * 0.4;
      const radius = 2.8;
      this.camera.position.x = Math.sin(this.autoTourTime) * radius;
      this.camera.position.z = Math.cos(this.autoTourTime) * radius + 0.8;
      this.camera.position.y = 1.3 + Math.sin(this.autoTourTime * 0.5) * 0.3;
      this.controls.target.set(0, 0.9, -0.2);
    } else {
      // Smooth lerp to camera preset
      this.camera.position.lerp(this.targetCameraPos, 0.05);
      this.currentLookAt.lerp(this.targetLookAt, 0.05);
      this.controls.target.copy(this.currentLookAt);
    }

    this.controls.update();

    // 2. Update Scene Entities
    this.computerScreen.update(deltaTime);
    this.workstation.update(deltaTime);
    this.room.update(deltaTime);
    this.hologram.update(deltaTime);

    // 3. Render
    this.renderer.render(this.scene, this.camera);
  }

  public destroy() {
    window.removeEventListener('resize', this.onResize);
    const dom = this.renderer.domElement;
    dom.removeEventListener('pointermove', this.onPointerMove);
    dom.removeEventListener('click', this.onClick);
    this.renderer.dispose();
  }
}
